import { jsx as _jsx, jsxs as _jsxs, Fragment as _Fragment } from "react/jsx-runtime";
import { Code, ConnectError } from '@connectrpc/connect';
import { SpecraMasterDetail } from '@mono/ui-components/admirals';
import { SpecraActionList } from '@mono/ui-components/captains';
import { SpecraBadge, SpecraButton, SpecraSpinner, SpecraText } from '@mono/ui-components/ensigns';
import { SpecraEmptyState, SpecraInlineBanner, SpecraPageHeader, SpecraSegmentedControl } from '@mono/ui-components/lieutenants';
import { Testids } from '@mono/ui-selectors';
import { useInfiniteQuery, useQuery, useQueryClient } from '@tanstack/react-query';
import { useSearchParams } from 'react-router-dom';
import { toast } from 'sonner';
import { useAuth } from '../../../contexts/authContext';
import { ErrorState } from '../../Delivery/_shared/components/PageStates';
import { registryKeys } from '../queryKeys';
import { RegistryRecordFrame } from '../RegistryRecordFrame';
import { registryRecordClient } from '../records/registryRecordClient';
import { ReviewDetail } from './ReviewDetail';
export const REVIEW_KINDS = {
    LCAT_CANDIDATE: 'Labor categories',
    MAPPING: 'Mappings',
    PERIOD_COERCION: 'Periods',
    ENTITY_MATCH: 'Entities'
};
export const itemKey = (item)=>`${item.kind}:${item.id}`;
const supported = (kind)=>Object.keys(REVIEW_KINDS).includes(kind);
export function matchesReviewFilters(item, anchor, vehicle) {
    const refs = [
        item.left?.ref,
        item.right?.ref,
        ...item.alternatives.map((s)=>s.ref)
    ];
    return (!anchor || refs.some((r)=>r?.kind === 'LCAT_ANCHOR' && r.id === anchor)) && (!vehicle || refs.some((r)=>r?.kind === 'VEHICLE' && r.id === vehicle));
}
export function RegistryReviewQueuePage() {
    const { user } = useAuth();
    const viewer = `${user?.tenant_id ?? ''}:${user?.id ?? ''}`;
    const qc = useQueryClient();
    const [params, setParams] = useSearchParams();
    const rawKind = params.get('kind') ?? '';
    const kind = supported(rawKind) ? rawKind : '';
    const anchor = params.get('anchor') ?? '';
    const vehicle = params.get('vehicle') ?? '';
    const selectedKey = params.get('item') ?? '';
    const [selectedKind, selectedId] = selectedKey.split(':');
    const queue = useInfiniteQuery({
        queryKey: [
            ...registryKeys.all,
            'review-queue',
            viewer,
            kind
        ],
        initialPageParam: '',
        queryFn: ({ pageParam })=>registryRecordClient.listRegistryReviewItems({
                kinds: kind ? [
                    kind
                ] : [],
                afterCursor: pageParam,
                limit: 50
            }),
        getNextPageParam: (last)=>last.nextCursor || undefined,
        retry: false
    });
    const items = (queue.data?.pages.flatMap((p)=>p.items) ?? []).filter((it)=>supported(it.kind) && matchesReviewFilters(it, anchor, vehicle));
    const selected = useQuery({
        queryKey: [
            ...registryKeys.all,
            'review-item',
            viewer,
            selectedKey
        ],
        queryFn: ()=>registryRecordClient.getRegistryReviewItem({
                kind: selectedKind,
                id: selectedId
            }),
        enabled: !!selectedId && supported(selectedKind) && (!kind || selectedKind === kind),
        retry: false
    });
    const selectedItem = selected.data?.item;
    const item = selectedKey ? selectedItem && matchesReviewFilters(selectedItem, anchor, vehicle) && (!kind || selectedItem.kind === kind) ? selectedItem : undefined : items[0];
    const pick = (key)=>{
        setParams((prev)=>{
            const next = new URLSearchParams(prev);
            key ? next.set('item', key) : next.delete('item');
            return next;
        });
    };
    const onRecorded = async (keepItem = false)=>{
        toast.success('Decision recorded');
        if (!keepItem && item) {
            const i = items.findIndex((it)=>itemKey(it) === itemKey(item));
            const next = items[i + 1] ?? items.find((it)=>itemKey(it) !== itemKey(item));
            pick(next ? itemKey(next) : '');
        }
        await Promise.all([
            qc.invalidateQueries({
                queryKey: registryKeys.all
            }),
            ...[
                'labor category candidates',
                'labor category',
                'lcat mappings',
                'rate observations',
                'pricing sheet review',
                'registry-records',
                'contact',
                'company profile',
                'contract vehicle',
                'government organization'
            ].map((key)=>qc.invalidateQueries({
                    queryKey: [
                        key
                    ]
                }))
        ]);
    };
    const counts = queue.data?.pages[0]?.counts ?? [];
    const missing = queue.error instanceof ConnectError && queue.error.code === Code.NotFound;
    return /*#__PURE__*/ _jsx(RegistryRecordFrame, {
        // Breadcrumb: Registries / Labor Categories / Review queue (the queue lives under that tab).
        kindLabel: "Labor Categories",
        tab: "lcats",
        title: "Review queue",
        status: queue.isPending ? 'loading' : missing ? 'unavailable' : queue.isError && !queue.data ? 'error' : 'ready',
        error: queue.error,
        onRetry: ()=>void queue.refetch(),
        header: /*#__PURE__*/ _jsx(SpecraPageHeader, {
            titleSize: "home",
            eyebrow: "Review queue",
            title: "Machines suggest, people decide",
            description: "Extraction, price-list parsing and the people graph propose changes with their evidence. A decision is recorded once, with who made it, and every page that uses the row benefits from then on."
        }),
        children: /*#__PURE__*/ _jsxs("div", {
            "data-testid": Testids.registryReview.page,
            className: "min-h-[var(--component-master-detail-min-height)]",
            children: [
                rawKind && !supported(rawKind) && /*#__PURE__*/ _jsx(SpecraInlineBanner, {
                    tone: "warning",
                    children: "That kind is unavailable. Showing supported review items."
                }),
                /*#__PURE__*/ _jsx(SpecraMasterDetail, {
                    toolbar: {
                        search: false,
                        filters: /*#__PURE__*/ _jsx(SpecraSegmentedControl, {
                            label: "Kind",
                            testId: Testids.registryReview.kindFilter,
                            value: kind,
                            options: [
                                {
                                    value: '',
                                    label: 'All',
                                    count: counts.filter((c)=>supported(c.kind)).reduce((n, c)=>n + c.count, 0)
                                },
                                ...Object.entries(REVIEW_KINDS).map(([value, label])=>({
                                        value,
                                        label,
                                        count: counts.find((c)=>c.kind === value)?.count ?? 0
                                    }))
                            ],
                            onValueChange: (value)=>setParams((prev)=>{
                                    const next = new URLSearchParams(prev);
                                    value ? next.set('kind', value) : next.delete('kind');
                                    next.delete('item');
                                    return next;
                                })
                        })
                    },
                    master: /*#__PURE__*/ _jsxs(_Fragment, {
                        children: [
                            /*#__PURE__*/ _jsx(SpecraActionList, {
                                trailing: null,
                                getItemTestId: ()=>Testids.registryReview.item,
                                items: items.map((it)=>({
                                        id: itemKey(it),
                                        label: it.title,
                                        description: it.subtitle,
                                        eyebrow: /*#__PURE__*/ _jsxs(_Fragment, {
                                            children: [
                                                /*#__PURE__*/ _jsx(SpecraBadge, {
                                                    children: REVIEW_KINDS[it.kind]
                                                }),
                                                ' ',
                                                it.sourceLabel
                                            ]
                                        }),
                                        selected: itemKey(it) === (item && itemKey(item)),
                                        onClick: ()=>pick(itemKey(it))
                                    }))
                            }),
                            items.length === 0 && /*#__PURE__*/ _jsx(SpecraEmptyState, {
                                "data-testid": Testids.registryReview.empty,
                                title: "Nothing to review.",
                                description: queue.hasNextPage ? 'No matching items on these pages. Load more to continue.' : 'New suggestions will appear here when they need your decision.'
                            }),
                            queue.isError && /*#__PURE__*/ _jsx(ErrorState, {
                                error: queue.error,
                                onRetry: ()=>void queue.refetch()
                            }),
                            queue.hasNextPage && /*#__PURE__*/ _jsx(SpecraButton, {
                                variant: "ghost",
                                "data-testid": Testids.registryReview.loadMore,
                                disabled: queue.isFetchingNextPage,
                                onClick: ()=>void queue.fetchNextPage(),
                                children: queue.isFetchingNextPage ? 'Loading…' : 'Load more'
                            })
                        ]
                    }),
                    detail: selectedKey && selected.isFetching ? /*#__PURE__*/ _jsx(SpecraSpinner, {
                        label: "Loading review item"
                    }) : selected.error instanceof ConnectError && selected.error.code === Code.NotFound ? /*#__PURE__*/ _jsx(SpecraEmptyState, {
                        title: "This item is no longer available",
                        description: "It may have been decided or removed. Choose another suggestion to continue.",
                        action: /*#__PURE__*/ _jsxs(_Fragment, {
                            children: [
                                /*#__PURE__*/ _jsx(SpecraButton, {
                                    variant: "secondary",
                                    onClick: ()=>void selected.refetch(),
                                    children: "Retry"
                                }),
                                /*#__PURE__*/ _jsx(SpecraButton, {
                                    variant: "ghost",
                                    onClick: ()=>pick(''),
                                    children: "Choose another suggestion"
                                })
                            ]
                        })
                    }) : selected.isError ? /*#__PURE__*/ _jsx(ErrorState, {
                        title: selected.error instanceof ConnectError && selected.error.code === Code.NotFound ? 'This item is no longer available' : 'This item could not be loaded',
                        error: selected.error,
                        onRetry: ()=>void selected.refetch()
                    }) : item ? /*#__PURE__*/ _jsx(ReviewDetail, {
                        item: item,
                        onRecorded: onRecorded
                    }, itemKey(item)) : selectedKey ? /*#__PURE__*/ _jsx(SpecraText, {
                        children: "This item is no longer in this view. Select another suggestion."
                    }) : null
                })
            ]
        })
    });
}
